/**
 * pulsebot/handlers.ts — the READ-ONLY answer handlers for PulseBot Phase 1A.
 *
 * Every handler is a plain async function (fetch-on-submit) with the uniform
 * signature (ctx: BotContext) => Promise<BotResult>. They reuse the app's
 * existing data sources:
 *   - myOpenTickets    → direct supabase query (replicates useTickets' query
 *                        as a plain fn, per the no-hook-in-handler rule)
 *   - overdueInvoices  → listInvoices() + the AR-aging "outstanding + past due"
 *                        logic replicated from ARAgingPage (due_date-driven,
 *                        local time — the manual 'overdue' label is NOT trusted)
 *   - pendingProcurement → direct supabase query
 *
 * ZERO writes in this phase. Each handler self-catches data errors and answers
 * gracefully rather than throwing into the chat.
 */

import { supabase } from '../supabase';
import { listInvoices, type Invoice } from '../invoices';
import { formatCurrency } from '../format';
import { readCapabilities, actionCapabilities, type Capability } from './capabilities';
import { hasAnyRole, FINANCE_ROLES, PROCUREMENT_ROLES } from './permissions';
import type { BotContext, BotResult, BotResultItem } from './types';

// ─── Help / what can you do ──────────────────────────────────────────────────
// A read-style intent that lists everything the bot can do, sourced from the
// single capabilities registry (capabilities.ts) so new executors appear here
// automatically — no second list to maintain.

/** Render one capability as a result row (phrase + description, role note → badge). */
function capabilityItem(c: Capability): BotResultItem {
  return {
    label: `"${c.phrase}"`,
    sublabel: c.description,
    badge: c.rolesNote,
  };
}

export async function helpCapabilities(ctx: BotContext): Promise<BotResult> {
  // Don't advertise a capability the bot would then refuse: a role-restricted
  // entry shows only to users whose role is in its allow-list (ungated entries
  // show for everyone). super_admin is in every list, so it sees all.
  const visible = (c: Capability) => !c.roles || hasAnyRole(ctx.profile?.role, c.roles);
  return {
    text: "Here's what I can do — just type one of these:",
    items: [
      { label: 'I can answer questions:' },
      ...readCapabilities().filter(visible).map(capabilityItem),
      { label: 'I can create things:' },
      ...actionCapabilities().filter(visible).map(capabilityItem),
    ],
  };
}

// ─── My open tickets ────────────────────────────────────────────────────────

/** Statuses that mean a ticket is closed out (everything else is "open"). */
const CLOSED_TICKET_STATUSES = ['resolved', 'closed'];

export async function myOpenTickets(ctx: BotContext): Promise<BotResult> {
  if (!ctx.profile) return { text: 'Please sign in to see your tickets.' };

  const { data, error } = await supabase
    .from('support_tickets')
    .select('ticket_number, title, status, priority')
    .eq('requester_id', ctx.profile.id)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[PulseBot] myOpenTickets failed', error);
    return { text: "Sorry — I couldn't load your tickets right now." };
  }

  const open = (data ?? []).filter(
    (t: { status: string }) => !CLOSED_TICKET_STATUSES.includes(t.status),
  );
  if (open.length === 0) return { text: 'You have no open tickets. 🎉' };

  return {
    text: `You have ${open.length} open ticket${open.length === 1 ? '' : 's'}:`,
    items: open.map((t: { ticket_number: number; title: string; status: string; priority: string }) => ({
      label: `TKT-${String(t.ticket_number).padStart(3, '0')} · ${t.title}`,
      sublabel: `Priority: ${t.priority}`,
      badge: t.status,
    })),
  };
}

// ─── Overdue invoices ───────────────────────────────────────────────────────
// Reuses ARAgingPage's rules: outstanding = status IN ('sent','overdue); aging
// is driven purely by due_date vs today in LOCAL time (the stored 'overdue'
// label is manual and untrusted). Helpers replicated here (they're not exported
// from ARAgingPage).

const OUTSTANDING_STATUSES = new Set<Invoice['status']>(['sent', 'overdue']);

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Today as YYYY-MM-DD in LOCAL time (UTC would mis-bucket at the day boundary). */
function localTodayStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** YYYY-MM-DD → integer day count (UTC midnight) for DST-safe day subtraction. */
function toDayNumber(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
}

/** Days past due = today − due_date. ≤ 0 means not yet due. */
function daysPastDue(dueDate: string, todayStr: string): number {
  return toDayNumber(todayStr) - toDayNumber(dueDate);
}

export async function overdueInvoices(ctx: BotContext): Promise<BotResult> {
  // Gate: invoices are finance-only to view (mirrors the /finance/* route). The
  // bot bypasses that route, and invoices RLS has no role check, so this client
  // gate is the control that stops a non-finance user listing invoice data.
  if (!hasAnyRole(ctx.profile?.role, FINANCE_ROLES)) {
    return { text: 'Overdue invoices are restricted to finance roles.' };
  }

  let rows: Invoice[];
  try {
    rows = await listInvoices(); // already excludes soft-deleted rows
  } catch (err) {
    console.error('[PulseBot] overdueInvoices failed', err);
    return { text: "Sorry — I couldn't load invoices right now." };
  }

  const currency = ctx.company?.currency || 'EUR';
  const today = localTodayStr();
  const overdue = rows
    .filter(inv => OUTSTANDING_STATUSES.has(inv.status))
    .map(inv => ({ inv, days: daysPastDue(inv.due_date, today) }))
    .filter(x => x.days > 0)
    .sort((a, b) => b.days - a.days); // most overdue first

  if (overdue.length === 0) return { text: 'No overdue invoices. 🎉' };

  return {
    text: `You have ${overdue.length} overdue invoice${overdue.length === 1 ? '' : 's'}:`,
    items: overdue.map(({ inv, days }) => ({
      label: `${inv.invoice_number} · ${inv.client_name}`,
      sublabel: `${formatCurrency(inv.amount || 0, currency, 2)} · ${days} day${days === 1 ? '' : 's'} overdue`,
      badge: inv.status,
    })),
  };
}

// ─── Pending procurement ────────────────────────────────────────────────────
// No procurement lib exists — query the table directly. Gated to admin roles
// (mirrors the /admin/procurement route); procurement_requests has no RLS, so
// this client gate is the control that stops non-admins listing requests.

export async function pendingProcurement(ctx: BotContext): Promise<BotResult> {
  if (!hasAnyRole(ctx.profile?.role, PROCUREMENT_ROLES)) {
    return { text: 'Pending procurement is restricted to admin roles.' };
  }

  const { data, error } = await supabase
    .from('procurement_requests')
    .select('request_number, item_name, estimated_cost, currency, status')
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[PulseBot] pendingProcurement failed', error);
    return { text: "Sorry — I couldn't load procurement requests right now." };
  }

  if (!data || data.length === 0) return { text: 'No pending purchase requests. 🎉' };

  const fallbackCurrency = ctx.company?.currency || 'EUR';
  return {
    text: `There ${data.length === 1 ? 'is' : 'are'} ${data.length} pending purchase request${data.length === 1 ? '' : 's'}:`,
    items: data.map((r: { request_number: string; item_name: string; estimated_cost: number; currency: string | null; status: string }) => ({
      label: `${r.request_number} · ${r.item_name}`,
      sublabel: formatCurrency(r.estimated_cost || 0, r.currency || fallbackCurrency, 2),
      badge: r.status,
    })),
  };
}
